import { useEffect, useRef, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { FEED_PAGE_SIZE } from "./useInfiniteScroll";

// Feed paginado desde Firestore para ofertas / eventos / autos / viajes:
// en vez de escuchar la colección entera (lo que había antes), se escucha
// orderBy("createdAt", "desc") con un limit que crece de a tandas, mismo
// patrón que Hilos.js. Sigue siendo onSnapshot, así que la lista se actualiza
// en vivo (contadores, publicaciones nuevas).
//
// El filtrado/orden fino (país, búsqueda, rangos, relevancia) sigue en el
// cliente sobre lo ya cargado. Por eso la página le avisa con
// useEnsureFeedCount cuántos resultados filtrados necesita mostrar, y si con
// lo cargado no alcanzan se piden más documentos hasta juntarlos o llegar al
// final de la colección.
//
// Uso:
//   const feed = usePagedFeed("offers", mapOfferDoc);
//   const filtered = useMemo(() => filtrar(feed.items), [...]);
//   const { limit, sentinelRef } = useInfiniteScroll(filtersKey, FEED_PAGE_SIZE);
//   useEnsureFeedCount(feed, filtered.length, limit);
//   {(filtered.length > limit || !feed.exhausted) && <Box ref={sentinelRef} />}
//
// `filters` (opcional) son condiciones de igualdad [campo, valor] que se
// agregan a la consulta, p. ej. [["isPrivate", false]] para eventos (las
// reglas exigen ese filtro para listar eventos; ver firestore.rules).
export const usePagedFeed = (
  collectionName,
  mapDoc = (d) => ({ id: d.id, ...d.data() }),
  pageSize = FEED_PAGE_SIZE,
  filters = []
) => {
  const [fetchLimit, setFetchLimit] = useState(pageSize);
  const [state, setState] = useState({ items: [], loadedLimit: 0, exhausted: false, loading: true });
  const mapDocRef = useRef(mapDoc);
  mapDocRef.current = mapDoc;
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    const conditions = JSON.parse(filtersKey).map(([field, value]) => where(field, "==", value));
    const q = query(collection(db, collectionName), ...conditions, orderBy("createdAt", "desc"), limit(fetchLimit));
    // Al agrandar el limit se reemplaza la suscripción, pero los items
    // anteriores se mantienen en pantalla hasta que llega el snapshot nuevo.
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setState({
          items: snapshot.docs.map((d) => mapDocRef.current(d)),
          loadedLimit: fetchLimit,
          exhausted: snapshot.size < fetchLimit,
          loading: false,
        });
      },
      (error) => {
        console.error(`Error cargando ${collectionName}:`, error);
        setState((prev) => ({ ...prev, loadedLimit: fetchLimit, exhausted: true, loading: false }));
      }
    );
    return () => unsubscribe();
  }, [collectionName, fetchLimit, filtersKey]);

  return {
    items: state.items,
    loading: state.loading,
    exhausted: state.exhausted,
    // true mientras se espera el snapshot de un limit más grande
    fetchingMore: state.loadedLimit !== fetchLimit,
    fetchLimit,
    setFetchLimit,
    pageSize,
  };
};

// Pide más documentos mientras los resultados filtrados no alcancen para
// mostrar `neededCount`. El salto crece con lo ya cargado (x1.5) para que una
// búsqueda con pocos resultados no haga decenas de viajes de a 13.
export const useEnsureFeedCount = (feed, availableCount, neededCount) => {
  const { loading, exhausted, fetchingMore, fetchLimit, setFetchLimit, pageSize } = feed;
  useEffect(() => {
    if (loading || exhausted || fetchingMore) return;
    if (availableCount >= neededCount) return;
    setFetchLimit(fetchLimit + Math.max(pageSize, Math.round(fetchLimit / 2)));
  }, [loading, exhausted, fetchingMore, fetchLimit, setFetchLimit, pageSize, availableCount, neededCount]);
};

// true mientras la lista filtrada todavía puede crecer con más documentos
// (para mostrar "cargando" en vez de "sin resultados").
export const feedMayHaveMore = (feed) => feed.loading || !feed.exhausted;
